import { Input } from "@/components/ui/input";
import { deleteWebPushSubscription, listWebPushSubscriptions, subscribeBrowserForWebPush, type WebPushSubscription } from "@/lib/webpush";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
interface WebPushSubscriptionSelectorProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  connectorId?: string;
  onConnectorSelect?: (connectorId: string) => void;
}
function subLabel(s: WebPushSubscription): string {
  if (s.label?.trim()) return s.label;
  const host = (() => {
    try {
      return new URL(s.endpoint).host;
    } catch {
      return s.endpoint.slice(0, 32);
    }
  })();
  const ua = s.userAgent?.split(" ")[0] ?? "browser";
  return `${ua} @ ${host}`;
}
export default function WebPushSubscriptionSelector({
  label,
  description,
  value,
  onChange,
  required,
  connectorId,
  onConnectorSelect
}: WebPushSubscriptionSelectorProps) {
  const [subs, setSubs] = useState<WebPushSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fetchSubs = useCallback(() => {
    setLoading(true);
    listWebPushSubscriptions(connectorId, 50, 0).then(result => setSubs(result.items)).catch(err => {
      console.error("Failed to load Web Push subscriptions", err);
      setSubs([]);
    }).finally(() => setLoading(false));
  }, [connectorId]);
  useEffect(() => {
    fetchSubs();
  }, [fetchSubs]);
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);
  const filtered = subs.filter(s => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (s.label || "").toLowerCase().includes(q) || (s.userAgent || "").toLowerCase().includes(q) || s.endpoint.toLowerCase().includes(q);
  });
  const selected = subs.find(s_0 => s_0.id === value);
  async function handleSubscribeThisBrowser() {
    setSubscribeError(null);
    if (!connectorId) {
      setSubscribeError("Pick a Web Push connector first.");
      return;
    }
    setSubscribing(true);
    try {
      const sub = await subscribeBrowserForWebPush(connectorId, newLabel.trim() || undefined);
      setSubs(prev => [sub, ...prev.filter(s_1 => s_1.id !== sub.id)]);
      onChange(sub.id);
      onConnectorSelect?.(sub.connectorId);
      setNewLabel("");
      setIsOpen(false);
    } catch (err_0) {
      setSubscribeError(err_0 instanceof Error ? err_0.message : "Failed to subscribe");
    } finally {
      setSubscribing(false);
    }
  }
  async function handleRemove(id: string) {
    try {
      await deleteWebPushSubscription(id);
      setSubs(prev_0 => prev_0.filter(s_2 => s_2.id !== id));
    } catch (err_1) {
      // toast layer not threaded here; log so operators can debug
      console.error("Failed to delete Web Push subscription", err_1);
    }
  }
  return <div className="w-full" ref={containerRef}>
      {label && <label className="field-label mb-1 block">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled mb-1 text-xs">{description}</p>}

      <button type="button" onClick={() => setIsOpen(o => !o)} className={cn("border-ds-border-default bg-ds-surface-primary flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm", isOpen && "ring-2 ring-blue-300")}>
        <span className="truncate">
          {selected ? subLabel(selected) : value ? value : <span className="text-ds-text-muted">Pick a subscribed browser…</span>}
        </span>
        <span className="text-ds-text-muted text-xs">▾</span>
      </button>

      {isOpen && <div className="border-ds-border-default bg-ds-surface-primary mt-1 max-h-96 w-full overflow-auto rounded-md border shadow-lg">
          <div className="border-ds-border-default border-b p-2">
            <Input autoFocus placeholder="Search subscriptions…" value={query} onChange={e_0 => setQuery(e_0.target.value)} />
          </div>

          {loading && <div className="text-ds-text-muted p-3 text-sm">Loading…</div>}

          {!loading && filtered.map(s_3 => <div key={s_3.id} className="hover:bg-ds-surface-hover flex items-center px-3">
                <button type="button" onClick={() => {
          onChange(s_3.id);
          if (s_3.connectorId) onConnectorSelect?.(s_3.connectorId);
          setIsOpen(false);
          setQuery("");
        }} className={cn("flex flex-1 items-center gap-2 py-2 text-left text-sm", s_3.id === value ? "bg-blue-50 text-blue-700" : "text-ds-text-primary")}>
                  <span className="min-w-0 flex-1 truncate">{subLabel(s_3)}</span>
                </button>
                <button type="button" onClick={() => handleRemove(s_3.id)} className="text-ds-text-muted pl-2 text-xs hover:text-red-500" title="Remove this subscription">
                  ✕
                </button>
              </div>)}

          {!loading && filtered.length === 0 && <div className="text-ds-text-muted px-3 py-2 text-sm">
              No subscriptions yet — subscribe this browser below.
            </div>}

          <div className="border-ds-border-default space-y-2 border-t p-3">
            <Input placeholder="Label this device (e.g. Mitch's laptop)" value={newLabel} onChange={e_1 => setNewLabel(e_1.target.value)} />
            {subscribeError && <div className="text-xs text-red-500">{subscribeError}</div>}
            <button type="button" disabled={subscribing || !connectorId} onClick={handleSubscribeThisBrowser} className="w-full rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50">
              {subscribing ? "Subscribing…" : "Subscribe this browser"}
            </button>
            {!connectorId && <p className="text-ds-text-muted text-xs">Pick a Web Push connector on the node first.</p>}
          </div>
        </div>}
    </div>;
}